import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

// Login error → always a string. The API can answer with an object detail
// (s158 mini-site people: {code:'minisite_only', domain}); rendering that raw
// crashed the login forms into a blank page.
export function loginErrorMessage(err, fallback = 'Invalid credentials') {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.code === 'minisite_only') return d.domain ? `This account belongs to ${d.domain}. Please sign in there.` : 'This account belongs to another site.';
  if (d.code === 'membership_lapsed') return 'Your membership has expired. Choose a plan to continue.';
  return typeof d.message === 'string' ? d.message : fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // s159: membership lapsed → NOT logged in; only a pay pass (sessionStorage mb_pay)
  const [payPass, setPayPass] = useState(() => { try { return sessionStorage.getItem('mb_pay') || ''; } catch (_) { return ''; } });
  const enterPayMode = useCallback((detail) => {
    try { sessionStorage.setItem('mb_pay', detail.pay_token); sessionStorage.setItem('mb_pay_src', detail.source || ''); } catch (_) { /* ignore */ }
    localStorage.removeItem('auth_token');
    setUser(null);
    setPayPass(detail.pay_token);
  }, []);
  const leavePayMode = useCallback(() => {
    try { sessionStorage.removeItem('mb_pay'); sessionStorage.removeItem('mb_pay_src'); } catch (_) { /* ignore */ }
    setPayPass('');
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.code === 'membership_lapsed') enterPayMode(d);
      else setUser(null);
    } finally {
      setLoading(false);
    }
  }, [enterPayMode]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, loginType) => {
    let res;
    try {
      res = await authAPI.login(email, password, loginType);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.code === 'membership_lapsed') {
        enterPayMode(d);
        // lands on the home page, where the pay modal is shown (no session exists)
        if (window.location.pathname !== '/') window.location.assign('/');
        const err = new Error(d.message); err.membershipLapsed = true; err.response = e.response;
        throw err;
      }
      throw e;
    }
    leavePayMode();
    const { token } = res.data;
    localStorage.setItem('auth_token', token);
    // Re-fetch from /auth/me so user object carries effective_permissions + cms_roles
    const me = await authAPI.me();
    setUser(me.data);
    return me.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {}
    localStorage.removeItem('auth_token');
    leavePayMode();
    setUser(null);
  };

  const setUserData = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setUserData, payPass, leavePayMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// `isAdmin` moved to its own dependency-free leaf module (lib/isAdmin.js) to avoid
// a circular-import init-order crash — import it from there, not here.
